import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    ArrowUp,
    Check,
    ChevronDown,
    Copy,
    Cpu,
    Download,
    ExternalLink,
    Globe,
    History as HistoryIcon,
    Image as ImageIcon,
    Loader2,
    Lock,
    MousePointerClick,
    Monitor,
    Paperclip,
    RefreshCw,
    Smartphone,
    Sparkles,
    Square,
    Tablet,
    X,
} from "lucide-react";
import { toast } from "sonner";
import { api, API, runGenerationJob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import AssetsDialog from "@/components/AssetsDialog";
import HistoryDialog from "@/components/HistoryDialog";
import DeployDialog from "@/components/DeployDialog";
import PricingDialog from "@/components/PricingDialog";

const DEVICE_WIDTHS = {
    desktop: "100%",
    tablet: "820px",
    mobile: "390px",
};

export default function Builder() {
    const { projectId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();
    const { billing, refreshBilling } = useAuth();

    const [project, setProject] = useState(null);
    const [messages, setMessages] = useState([]);
    const [prompt, setPrompt] = useState("");
    const [generating, setGenerating] = useState(false);
    const [streamText, setStreamText] = useState("");
    const [device, setDevice] = useState("desktop");
    const [iframeKey, setIframeKey] = useState(0);
    const [streamingHtml, setStreamingHtml] = useState(null);
    const [publishOpen, setPublishOpen] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [copiedUrl, setCopiedUrl] = useState(false);
    const [models, setModels] = useState([]);
    const [selectedModel, setSelectedModel] = useState(null);
    const [assetsOpen, setAssetsOpen] = useState(false);
    const [assetCount, setAssetCount] = useState(null);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [previewingVersion, setPreviewingVersion] = useState(null); // {version_number, html}
    const [pricingOpen, setPricingOpen] = useState(false);
    const [attachedImage, setAttachedImage] = useState(null); // { asset_id, url, filename }
    const [uploadingImage, setUploadingImage] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [pendingEdit, setPendingEdit] = useState(null); // {selector, snippet, label}
    const [editPrompt, setEditPrompt] = useState("");
    const [mobileChatVh, setMobileChatVh] = useState(() => {
        if (typeof window === "undefined") return 40;
        const stored = parseFloat(localStorage.getItem("loom.mobileChatVh") || "");
        return Number.isFinite(stored) && stored >= 15 && stored <= 75 ? stored : 40;
    });
    const dragStateRef = useRef(null);
    const abortRef = useRef(null);
    const attachInputRef = useRef(null);
    const modelInitedRef = useRef(false);
    const chatEndRef = useRef(null);
    const initialFired = useRef(false);
    const editInputRef = useRef(null);

    useEffect(() => {
        if (!authLoading && !user) navigate("/", { replace: true });
    }, [authLoading, user, navigate]);

    const load = useCallback(async () => {
        try {
            const data = await api.getProject(projectId);
            setProject(data.project);
            setMessages(data.messages || []);
        } catch (e) {
            toast.error(e.message || "Could not load project");
            navigate("/dashboard");
        }
    }, [projectId, navigate]);

    useEffect(() => {
        if (user) load();
    }, [user, load]);

    // Load available models
    useEffect(() => {
        api.listModels().then((res) => {
            setModels(res.models);
            setSelectedModel((prev) => prev || res.default);
        }).catch(() => {});
    }, []);

    // Load asset count once so the top-bar badge is accurate on first render
    useEffect(() => {
        if (!user) return;
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assets`, { credentials: "include" })
            .then((r) => (r.ok ? r.json() : []))
            .then((list) => setAssetCount(Array.isArray(list) ? list.length : 0))
            .catch(() => setAssetCount(0));
    }, [user]);

    // On first project load, adopt whatever model is persisted on the project
    useEffect(() => {
        if (project?.model && !modelInitedRef.current) {
            modelInitedRef.current = true;
            setSelectedModel(project.model);
        }
    }, [project]);

    // Auto-run initial prompt if passed from Dashboard
    useEffect(() => {
        if (!project || initialFired.current) return;
        const initial = location.state?.initialPrompt;
        if (initial && messages.length === 0) {
            initialFired.current = true;
            runGenerate(initial);
        }
    }, [project, messages, location.state]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, streamText]);

    // Click-to-edit: listen for postMessage from the preview iframe.
    useEffect(() => {
        const handler = (e) => {
            if (!e?.data || e.data.type !== "loom-edit-click") return;
            setPendingEdit({
                selector: e.data.selector,
                label: e.data.label || e.data.selector,
                snippet: e.data.snippet || "",
            });
            setTimeout(() => editInputRef.current?.focus(), 60);
        };
        window.addEventListener("message", handler);
        return () => window.removeEventListener("message", handler);
    }, []);

    // Exiting edit mode should clear any pending selection.
    useEffect(() => {
        if (!editMode) {
            setPendingEdit(null);
            setEditPrompt("");
        }
    }, [editMode]);

    // Mobile chat/preview divider — pointer drag to resize
    const startDividerDrag = (e) => {
        e.preventDefault();
        const startY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
        const startVh = mobileChatVh;
        const vh = window.innerHeight;
        dragStateRef.current = { startY, startVh, vh };

        const onMove = (ev) => {
            const y = ev.clientY ?? ev.touches?.[0]?.clientY ?? 0;
            const deltaVh = ((y - dragStateRef.current.startY) / dragStateRef.current.vh) * 100;
            const next = Math.max(15, Math.min(75, dragStateRef.current.startVh + deltaVh));
            setMobileChatVh(next);
        };
        const onEnd = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onEnd);
            window.removeEventListener("pointercancel", onEnd);
            const final = dragStateRef.current?.startVh;
            dragStateRef.current = null;
            // Persist final value
            setMobileChatVh((cur) => {
                try { localStorage.setItem("loom.mobileChatVh", String(cur)); } catch { /* noop */ }
                return cur;
            });
            if (final === undefined) return;
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onEnd);
        window.addEventListener("pointercancel", onEnd);
    };

    const submitEdit = async () => {
        if (!pendingEdit || !editPrompt.trim() || generating) return;
        const p = editPrompt.trim();
        const target = pendingEdit;
        setEditPrompt("");
        setPendingEdit(null);
        setEditMode(false);
        setIframeKey((k) => k + 1); // strip the injected script for the incoming stream
        await runGenerate(p, {
            targetSelector: target.selector,
            targetSnippet: target.snippet,
        });
    };

    const runGenerate = async (raw, opts = {}) => {
        const { targetSelector, targetSnippet, mode } = opts;
        const p = (raw ?? prompt).trim();
        if ((!p && !attachedImage) || generating) return;
        // Opus 4.7 is Pro-only — open pricing dialog instead of hitting the API
        if (selectedModel === "opus-4-7" && !billing?.pro) {
            setPricingOpen(true);
            toast("Claude Opus 4.7 is a Pro feature — upgrade to unlock.");
            return;
        }
        const promptToSend = p || "Recreate the design shown in the attached reference image as a complete, self-contained website.";
        const imageAssetId = attachedImage?.asset_id;
        setPrompt("");
        setAttachedImage(null);
        setPreviewingVersion(null);
        setGenerating(true);
        setStreamText("");
        // Optimistically show the user message
        setMessages((prev) => [
            ...prev,
            {
                message_id: `tmp_${Date.now()}`,
                project_id: projectId,
                role: "user",
                content: targetSelector
                    ? `[edit \`${targetSelector}\`] ${promptToSend}`
                    : mode === "continue"
                    ? `[continue] ${promptToSend}`
                    : (imageAssetId ? `[image] ${promptToSend}` : promptToSend),
                created_at: new Date().toISOString(),
            },
        ]);

        let accumulated = "";
        const controller = new AbortController();
        abortRef.current = controller;
        await runGenerationJob(projectId, promptToSend, {
            model: selectedModel,
            image_asset_id: imageAssetId,
            target_selector: targetSelector,
            target_snippet: targetSnippet,
            mode: mode,
            signal: controller.signal,
            onProject: (proj) => {
                // Live-update the preview from the server-side checkpoints
                setProject((prev) => (prev ? { ...prev, html: proj.html, streaming: proj.streaming } : proj));
                setStreamingHtml(proj.html || null);
            },
            onStatus: (jobStatus) => {
                if (jobStatus.html_length && !accumulated) accumulated = "…";
                setStreamText(`▍ ${jobStatus.status} · ${(jobStatus.html_length || 0).toLocaleString()} bytes`);
            },
            onDone: async (evt) => {
                setStreamText("");
                setStreamingHtml(null);
                setGenerating(false);
                try {
                    const fresh = await api.getProject(projectId);
                    setProject(fresh.project);
                    setMessages(fresh.messages || []);
                    setIframeKey((k) => k + 1);
                } catch (err) {
                    console.warn("post-generate refresh failed:", err?.message || err);
                }
                if (evt?.truncated) {
                    toast.warning(
                        "Response was cut off — the site was saved as-is. Click Continue to finish it.",
                        { duration: 8000 },
                    );
                }
            },
            onError: (err) => {
                setGenerating(false);
                setStreamText("");
                setStreamingHtml(null);
                toast.error(err.message || "Generation failed");
            },
        });
    };

    const stopGenerate = () => {
        if (abortRef.current) {
            abortRef.current.abort();
            abortRef.current = null;
            setGenerating(false);
            setStreamText("");
            setStreamingHtml(null);
        }
    };

    const handleAttachImage = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Only images can be attached as references");
            return;
        }
        const MAX_MB = 15;
        if (file.size > MAX_MB * 1024 * 1024) {
            const actualMb = (file.size / (1024 * 1024)).toFixed(1);
            toast.error(`Image is ${actualMb} MB — must be under ${MAX_MB} MB. Try a lower-resolution screenshot or crop it first.`);
            return;
        }
        setUploadingImage(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assets`, {
                method: "POST",
                credentials: "include",
                body: fd,
            });
            if (!res.ok) {
                let d;
                try { d = (await res.json()).detail; } catch { d = res.statusText; }
                throw new Error(d);
            }
            const uploaded = await res.json();
            setAttachedImage(uploaded);
        } catch (e) {
            toast.error(e.message || "Upload failed");
        } finally {
            setUploadingImage(false);
        }
    };

    const handleKeyDown = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            runGenerate();
        }
    };

    const copyHtml = async () => {
        if (!project?.html) return;
        try {
            await navigator.clipboard.writeText(project.html);
            toast.success("HTML copied");
        } catch {
            toast.error("Copy failed");
        }
    };

    const downloadHtml = () => {
        if (!project?.html) return;
        const blob = new Blob([project.html], { type: "text/html" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${(project.name || "site").replace(/[^a-z0-9-_]+/gi, "-").toLowerCase()}.html`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const previewSrcDoc = useMemo(() => {
        let html = "";
        if (previewingVersion) html = previewingVersion.html;
        else if (streamingHtml) html = streamingHtml;
        else if (project) html = project.html || "";
        if (!html) return "";

        // Click-to-edit: when editMode is on and we're viewing the real project
        // (not a streaming or version preview), inject a click-capture script
        // that posts the clicked element's selector back to the parent frame.
        if (editMode && !streamingHtml && !previewingVersion) {
            const script = `\n<style id="loom-edit-mode-style">
                *[data-loom-edit-hover] { outline: 2px dashed #ff6b35 !important; outline-offset: 2px !important; cursor: pointer !important; }
                *[data-loom-edit-selected] { outline: 3px solid #ff6b35 !important; outline-offset: 2px !important; }
                html, body { cursor: crosshair !important; }
            </style>
            <script>(function(){
                var lastHover = null;
                var selected = null;
                function selectorFor(el){
                    if (!el || el === document.documentElement || el === document.body) return null;
                    if (el.id) return '#' + CSS.escape(el.id);
                    var path = [];
                    var cur = el;
                    while (cur && cur.nodeType === 1 && cur !== document.body && path.length < 6) {
                        var seg = cur.tagName.toLowerCase();
                        if (cur.classList && cur.classList.length) {
                            var cls = Array.from(cur.classList).filter(function(c){return !/^loom-|^ng-|^is-active/.test(c);}).slice(0,2).join('.');
                            if (cls) seg += '.' + cls;
                        }
                        var parent = cur.parentElement;
                        if (parent) {
                            var siblings = Array.from(parent.children).filter(function(x){return x.tagName === cur.tagName;});
                            if (siblings.length > 1) seg += ':nth-of-type(' + (siblings.indexOf(cur)+1) + ')';
                        }
                        path.unshift(seg);
                        if (cur.id) { path[0] = '#' + CSS.escape(cur.id); break; }
                        cur = cur.parentElement;
                    }
                    return path.join(' > ');
                }
                function labelFor(el){
                    var tag = el.tagName.toLowerCase();
                    var txt = (el.textContent || '').trim().replace(/\\s+/g,' ').slice(0, 40);
                    return '<' + tag + '>' + (txt ? ' · ' + txt : '');
                }
                document.addEventListener('mouseover', function(e){
                    if (lastHover) lastHover.removeAttribute('data-loom-edit-hover');
                    if (e.target && e.target !== document.body && e.target !== document.documentElement) {
                        e.target.setAttribute('data-loom-edit-hover', '');
                        lastHover = e.target;
                    }
                }, true);
                document.addEventListener('mouseout', function(){
                    if (lastHover) { lastHover.removeAttribute('data-loom-edit-hover'); lastHover = null; }
                }, true);
                document.addEventListener('click', function(e){
                    e.preventDefault(); e.stopPropagation();
                    if (selected) selected.removeAttribute('data-loom-edit-selected');
                    selected = e.target;
                    selected.setAttribute('data-loom-edit-selected', '');
                    var sel = selectorFor(e.target);
                    if (!sel) return;
                    window.parent.postMessage({
                        type: 'loom-edit-click',
                        selector: sel,
                        label: labelFor(e.target),
                        snippet: e.target.outerHTML.slice(0, 1500)
                    }, '*');
                }, true);
            })();</` + `script>`;
            if (/<\/body>/i.test(html)) return html.replace(/<\/body>/i, script + '</body>');
            return html + script;
        }
        return html;
    }, [project, streamingHtml, previewingVersion, editMode]);

    const doPublish = async () => {
        setPublishing(true);
        try {
            const res = await api.publishProject(projectId);
            setProject((prev) =>
                prev ? { ...prev, slug: res.slug, published: true, public_url: res.public_url } : prev,
            );
            toast.success("Site published");
        } catch (e) {
            toast.error(e.message || "Publish failed");
        } finally {
            setPublishing(false);
        }
    };

    const doUnpublish = async () => {
        setPublishing(true);
        try {
            await api.unpublishProject(projectId);
            setProject((prev) => (prev ? { ...prev, published: false } : prev));
            toast.success("Site unpublished");
        } catch (e) {
            toast.error(e.message || "Unpublish failed");
        } finally {
            setPublishing(false);
        }
    };

    const publicUrl = useMemo(() => {
        if (!project?.slug) return "";
        // Share URL goes through the backend `/api/share/:slug` endpoint so that
        // no-JS social crawlers (WhatsApp, Twitterbot, Facebook, iMessage) see
        // OG meta baked in server-side. Humans are auto-redirected to the pretty
        // /s/:slug wrapper.
        return `${API}/share/${project.slug}`;
    }, [project?.slug]);

    const copyPublicUrl = async () => {
        if (!publicUrl) return;
        try {
            await navigator.clipboard.writeText(publicUrl);
            setCopiedUrl(true);
            setTimeout(() => setCopiedUrl(false), 1600);
        } catch {
            toast.error("Copy failed");
        }
    };

    if (authLoading || !project) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <span className="overline">▍ Loading canvas…</span>
            </div>
        );
    }

    return (
        <div className="h-screen flex flex-col bg-background text-foreground overflow-hidden">
            {/* Top bar */}
            <div className="h-14 border-b border-border flex items-center justify-between px-3 md:px-4 gap-2 shrink-0 overflow-hidden">
                <div className="flex items-center gap-3 md:gap-4 min-w-0">
                    <button
                        data-testid="back-to-dashboard"
                        onClick={() => navigate("/dashboard")}
                        className="w-9 h-9 shrink-0 border border-border grid place-items-center hover:bg-secondary transition-colors"
                        title="Back to dashboard"
                    >
                        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                    <div className="hidden sm:block min-w-0">
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground truncate">
                            ▍ {project.project_id.replace("prj_", "").slice(0, 8)}
                        </div>
                        <div className="font-black tracking-tight text-base leading-tight truncate">{project.name}</div>
                    </div>
                </div>

                <div className="flex items-center gap-2 border border-border p-1">
                    <Cpu className="w-3.5 h-3.5 ml-1 text-muted-foreground" strokeWidth={1.5} />
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                data-testid="model-picker-trigger"
                                disabled={generating}
                                className="inline-flex items-center gap-2 h-7 px-2 font-mono text-[10px] uppercase tracking-[0.16em] hover:bg-secondary disabled:opacity-40 transition-colors"
                            >
                                <span data-testid="model-current">
                                    {models.find((m) => m.key === selectedModel)?.label || "Loading…"}
                                </span>
                                <ChevronDown className="w-3 h-3" strokeWidth={1.5} />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="end"
                            className="rounded-none border-foreground min-w-[280px] p-0"
                            data-testid="model-picker-menu"
                        >
                            <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground py-3">
                                ▍ Model
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator className="my-0 bg-border" />
                            {models.map((m) => {
                                const active = m.key === selectedModel;
                                const locked = m.key === "opus-4-7" && !billing?.pro;
                                return (
                                    <DropdownMenuItem
                                        key={m.key}
                                        data-testid={`model-option-${m.key}`}
                                        onSelect={(e) => {
                                            if (locked) {
                                                e.preventDefault();
                                                setPricingOpen(true);
                                                return;
                                            }
                                            setSelectedModel(m.key);
                                        }}
                                        className="rounded-none px-3 py-3 focus:bg-secondary cursor-pointer flex flex-col items-start gap-1"
                                    >
                                        <div className="flex items-center justify-between w-full">
                                            <span className="font-black text-sm tracking-tight">{m.label}</span>
                                            {locked ? (
                                                <span
                                                    data-testid={`model-lock-${m.key}`}
                                                    className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.16em] text-accent"
                                                >
                                                    <Lock className="w-3 h-3" strokeWidth={1.5} /> Pro
                                                </span>
                                            ) : active ? (
                                                <Check className="w-3.5 h-3.5 text-accent" strokeWidth={2} />
                                            ) : null}
                                        </div>
                                        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                                            {m.tagline}
                                        </span>
                                    </DropdownMenuItem>
                                );
                            })}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                <div className="hidden md:flex items-center gap-1 border border-border">
                    {[
                        { k: "desktop", Icon: Monitor },
                        { k: "tablet", Icon: Tablet },
                        { k: "mobile", Icon: Smartphone },
                    ].map(({ k, Icon }) => (
                        <button
                            key={k}
                            data-testid={`device-${k}`}
                            onClick={() => setDevice(k)}
                            className={`w-9 h-9 grid place-items-center transition-colors ${
                                device === k ? "bg-foreground text-background" : "hover:bg-secondary"
                            }`}
                            title={k}
                        >
                            <Icon className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                    ))}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    <div className="hidden md:flex items-center gap-2">
                        <button
                            data-testid="refresh-preview"
                            onClick={() => setIframeKey((k) => k + 1)}
                            className="btn-outline-brutal !h-9 !px-3 !text-[10px]"
                            title="Refresh preview"
                        >
                            <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                        <button
                            data-testid="copy-html"
                            onClick={copyHtml}
                            className="btn-outline-brutal !h-9 !px-3 !text-[10px]"
                        >
                            <Copy className="w-3.5 h-3.5" strokeWidth={1.5} /> Copy
                        </button>
                        <button
                            data-testid="download-html"
                            onClick={downloadHtml}
                            className="btn-outline-brutal !h-9 !px-3 !text-[10px]"
                        >
                            <Download className="w-3.5 h-3.5" strokeWidth={1.5} /> HTML
                        </button>
                        <button
                            data-testid="open-history"
                            onClick={() => setHistoryOpen(true)}
                            className="btn-outline-brutal !h-9 !px-3 !text-[10px]"
                            title="Version history"
                        >
                            <HistoryIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
                            History
                        </button>
                        <button
                            data-testid="open-assets"
                            onClick={() => setAssetsOpen(true)}
                            className="btn-outline-brutal !h-9 !px-3 !text-[10px]"
                            title="Asset library"
                        >
                            <ImageIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
                            Assets
                            {assetCount !== null && assetCount > 0 && (
                                <span className="ml-1 font-mono text-[9px] text-muted-foreground">
                                    {assetCount}
                                </span>
                            )}
                        </button>
                    </div>
                    <button
                        data-testid="toggle-edit-mode"
                        onClick={() => setEditMode((v) => !v)}
                        title={editMode ? "Exit click-to-edit mode" : "Click any element to edit just that section"}
                        aria-pressed={editMode}
                        className={`inline-flex items-center gap-1.5 h-9 px-3 border rounded font-mono text-[10px] uppercase tracking-[0.16em] transition-colors ${
                            editMode
                                ? "border-accent bg-accent/15 text-accent shadow-[0_0_0_1px_hsl(35_92%_68%/0.35)]"
                                : "border-border hover:bg-secondary"
                        }`}
                    >
                        <MousePointerClick className="w-3.5 h-3.5" strokeWidth={1.5} />
                        <span className="hidden md:inline">{editMode ? "Editing" : "Edit"}</span>
                    </button>
                    <button
                        data-testid="open-publish"
                        onClick={() => setPublishOpen(true)}
                        className="inline-flex items-center gap-2 h-9 px-3 md:px-4 border border-primary bg-primary text-primary-foreground rounded font-mono text-[10px] uppercase tracking-[0.16em] hover:bg-accent hover:border-accent transition-all duration-200 shadow-[0_6px_28px_-8px_hsl(14_100%_62%/0.55)] hover:shadow-[0_10px_40px_-8px_hsl(14_100%_62%/0.75)] hover:-translate-y-[1px]"
                    >
                        <Globe className="w-3.5 h-3.5" strokeWidth={1.5} />
                        Deploy
                    </button>
                </div>
            </div>

            {/* Main grid — on mobile: chat pinned to ~40vh, preview takes the rest */}
            <div className="flex-1 flex flex-col md:grid md:grid-cols-12 min-h-0">
                {/* Chat column */}
                <aside
                    style={{ height: `${mobileChatVh}vh` }}
                    className="flex-none md:!h-auto md:col-span-4 lg:col-span-3 border-b md:border-b-0 md:border-r border-border flex flex-col min-h-0 bg-background"
                >
                    <div className="h-10 border-b border-border px-4 flex items-center justify-between shrink-0">
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">▍ Conversation</div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                            {messages.length} msg
                        </div>
                    </div>
                    <div
                        data-testid="chat-scroll"
                        className="flex-1 overflow-y-auto loom-scroll p-4 space-y-4"
                    >
                        {messages.length === 0 && !generating && (
                            <div className="border border-dashed border-border p-6 text-center">
                                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                    ▍ Empty canvas — write a prompt below
                                </div>
                            </div>
                        )}
                        {messages.map((m) => (
                            <ChatBubble key={m.message_id} role={m.role} content={m.content} />
                        ))}
                        {generating && (
                            <ChatBubble
                                role="assistant"
                                content={streamText || "Thinking…"}
                                streaming
                            />
                        )}
                        <div ref={chatEndRef} />
                    </div>
                    <div className="border-t border-border p-3 shrink-0">
                        <div className="border border-border rounded focus-within:border-primary/60 transition-colors">
                            {attachedImage && (
                                <div
                                    data-testid="attached-image-chip"
                                    className="flex items-center gap-2 px-3 py-2 border-b border-border bg-secondary/60"
                                >
                                    <img
                                        src={attachedImage.url}
                                        alt="reference"
                                        className="w-10 h-10 object-cover rounded"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary">
                                            ▍ Reference attached
                                        </div>
                                        <div className="text-xs truncate text-muted-foreground" title={attachedImage.filename}>
                                            {attachedImage.filename}
                                        </div>
                                    </div>
                                    <button
                                        data-testid="clear-attached-image"
                                        onClick={() => setAttachedImage(null)}
                                        className="w-6 h-6 grid place-items-center border border-border hover:border-foreground text-muted-foreground hover:text-foreground transition-colors rounded"
                                        title="Remove"
                                    >
                                        <X className="w-3 h-3" strokeWidth={1.5} />
                                    </button>
                                </div>
                            )}
                            <textarea
                                data-testid="chat-input"
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                onKeyDown={handleKeyDown}
                                rows={3}
                                placeholder={
                                    attachedImage
                                        ? "Add instructions (or leave empty to recreate the image as-is)…"
                                        : messages.length === 0
                                            ? "Describe the site you want…"
                                            : "Ask for an iteration — 'make the hero taller', 'add pricing'…"
                                }
                                className="w-full bg-transparent p-3 text-sm resize-none focus:outline-none placeholder:text-muted-foreground"
                                disabled={generating}
                            />
                            <div className="border-t border-border px-3 h-10 flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                    <input
                                        ref={attachInputRef}
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        data-testid="attach-image-input"
                                        onChange={(e) => {
                                            handleAttachImage(e.target.files?.[0]);
                                            e.target.value = "";
                                        }}
                                    />
                                    <button
                                        data-testid="attach-image-button"
                                        onClick={() => attachInputRef.current?.click()}
                                        disabled={generating || uploadingImage}
                                        title="Attach reference image (image-to-site)"
                                        className="w-7 h-7 grid place-items-center border border-border hover:border-primary hover:text-primary text-muted-foreground disabled:opacity-40 transition-colors rounded"
                                    >
                                        {uploadingImage ? (
                                            <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} />
                                        ) : (
                                            <Paperclip className="w-3.5 h-3.5" strokeWidth={1.5} />
                                        )}
                                    </button>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                        ⌘/Ctrl+Enter
                                    </div>
                                </div>
                                <button
                                    data-testid={generating ? "stop-generation" : "send-prompt"}
                                    onClick={() => (generating ? stopGenerate() : runGenerate())}
                                    disabled={!generating && !prompt.trim() && !attachedImage}
                                    className="inline-flex items-center gap-2 h-7 px-3 bg-foreground text-background font-mono text-[10px] uppercase tracking-[0.16em] hover:bg-primary hover:text-primary-foreground disabled:opacity-40 disabled:hover:bg-foreground disabled:hover:text-background transition-colors rounded"
                                >
                                    {generating ? (
                                        <>
                                            <Square className="w-3 h-3" strokeWidth={1.5} fill="currentColor" /> Stop
                                        </>
                                    ) : (
                                        <>
                                            Send <ArrowUp className="w-3 h-3" strokeWidth={1.5} />
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </aside>

                {/* Mobile-only drag divider between chat & preview */}
                <div
                    data-testid="mobile-divider"
                    role="separator"
                    aria-orientation="horizontal"
                    aria-label="Resize chat and preview"
                    onPointerDown={startDividerDrag}
                    onDoubleClick={() => {
                        setMobileChatVh(40);
                        try { localStorage.setItem("loom.mobileChatVh", "40"); } catch { /* noop */ }
                    }}
                    className="md:hidden h-4 shrink-0 -mt-2 flex items-center justify-center cursor-ns-resize bg-background border-b border-border touch-none select-none"
                    style={{ touchAction: "none" }}
                >
                    <span className="block w-10 h-1 rounded-full bg-border/70 hover:bg-primary/70 transition-colors" />
                </div>

                {/* Preview column */}
                <main className="flex-1 md:col-span-8 lg:col-span-9 flex flex-col min-h-0 bg-secondary">
                    <div className="h-10 border-b border-border px-3 md:px-4 flex items-center justify-between gap-2 shrink-0 bg-background">
                        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground shrink-0">
                            <span className="w-1.5 h-1.5 bg-accent" />
                            {generating ? "Streaming…" : "Preview"}
                        </div>
                        <div className="hidden sm:block font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground truncate max-w-[60%]">
                            preview://{project.project_id}
                        </div>
                        <a
                            href={api.previewUrl(project.project_id)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded border border-primary text-primary bg-primary/10 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-primary hover:text-primary-foreground transition-colors shadow-[0_0_0_1px_hsl(14_100%_62%/0.15),0_4px_20px_-6px_hsl(14_100%_62%/0.4)]"
                            data-testid="open-preview-tab"
                            title={project.published ? "Live — click to open published site" : "Not published — preview only"}
                        >
                            <span
                                data-testid="live-status-indicator"
                                data-live={project.published ? "true" : "false"}
                                className="relative inline-flex h-2 w-2 shrink-0"
                            >
                                {project.published && (
                                    <span className="absolute inset-0 inline-flex rounded-full bg-green-400 opacity-75 animate-ping" />
                                )}
                                <span
                                    className={`relative inline-flex rounded-full h-2 w-2 ${
                                        project.published
                                            ? "bg-green-400 shadow-[0_0_6px_1px_rgba(74,222,128,0.7)]"
                                            : "bg-muted-foreground/40"
                                    }`}
                                />
                            </span>
                            <span>{project.published ? "Live" : "Open"}</span>
                            <ExternalLink className="w-3 h-3" strokeWidth={1.75} />
                        </a>
                    </div>
                    {previewingVersion && (
                        <div
                            data-testid="version-preview-banner"
                            className="border-b border-foreground bg-accent text-accent-foreground px-4 h-10 flex items-center justify-between shrink-0"
                        >
                            <div className="font-mono text-[10px] uppercase tracking-[0.2em]">
                                ▍ Previewing v{previewingVersion.version_number} — not yet current
                            </div>
                            <button
                                data-testid="exit-version-preview"
                                onClick={() => setPreviewingVersion(null)}
                                className="inline-flex items-center gap-1 h-7 px-3 border border-accent-foreground font-mono text-[10px] uppercase tracking-[0.16em] hover:bg-accent-foreground hover:text-accent transition-colors"
                            >
                                <X className="w-3 h-3" strokeWidth={1.5} /> Exit preview
                            </button>
                        </div>
                    )}
                    {project?.streaming && !generating && !previewingVersion && (
                        <div
                            data-testid="interrupted-stream-banner"
                            className="border-b border-primary bg-primary/10 px-3 md:px-4 py-2 flex flex-wrap items-center justify-between gap-2 shrink-0"
                        >
                            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-primary">
                                <Loader2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                                ▍ Previous generation was interrupted — partial site was saved.
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    data-testid="dismiss-interrupted-banner"
                                    onClick={async () => {
                                        try {
                                            await api.dismissStreamingFlag(projectId);
                                            setProject((p) => (p ? { ...p, streaming: false } : p));
                                        } catch { /* noop */ }
                                    }}
                                    className="inline-flex items-center gap-1 h-7 px-3 border border-primary/50 text-primary font-mono text-[10px] uppercase tracking-[0.16em] hover:bg-primary/20 transition-colors"
                                >
                                    <X className="w-3 h-3" strokeWidth={1.5} /> Dismiss
                                </button>
                                <button
                                    data-testid="resume-generation"
                                    onClick={() => runGenerate(
                                        "Finish the site — add the remaining sections and close out the HTML.",
                                        { mode: "continue" },
                                    )}
                                    className="inline-flex items-center gap-1.5 h-7 px-3 border border-primary bg-primary text-primary-foreground font-mono text-[10px] uppercase tracking-[0.16em] hover:brightness-110 transition-all"
                                >
                                    <Sparkles className="w-3 h-3" strokeWidth={1.5} />
                                    Continue
                                </button>
                            </div>
                        </div>
                    )}
                    {editMode && !previewingVersion && (
                        <div
                            data-testid="edit-mode-banner"
                            className="border-b border-accent bg-accent/10 px-3 md:px-4 py-2 shrink-0 space-y-2"
                        >
                            {!pendingEdit ? (
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-accent">
                                        <MousePointerClick className="w-3.5 h-3.5" strokeWidth={1.5} />
                                        ▍ Click any element in the preview to edit only that section
                                    </div>
                                    <button
                                        data-testid="exit-edit-mode"
                                        onClick={() => setEditMode(false)}
                                        className="inline-flex items-center gap-1 h-7 px-3 border border-accent text-accent font-mono text-[10px] uppercase tracking-[0.16em] hover:bg-accent hover:text-accent-foreground transition-colors"
                                    >
                                        <X className="w-3 h-3" strokeWidth={1.5} /> Cancel
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between gap-2">
                                        <div
                                            data-testid="edit-target-label"
                                            className="font-mono text-[10px] uppercase tracking-[0.16em] text-accent truncate"
                                            title={pendingEdit.selector}
                                        >
                                            ▍ Editing {pendingEdit.label}
                                        </div>
                                        <button
                                            data-testid="clear-edit-target"
                                            onClick={() => { setPendingEdit(null); setEditPrompt(""); }}
                                            className="inline-flex items-center h-6 px-2 border border-accent text-accent font-mono text-[10px] uppercase tracking-[0.16em] hover:bg-accent hover:text-accent-foreground transition-colors"
                                            title="Pick a different element"
                                        >
                                            <X className="w-3 h-3" strokeWidth={1.5} />
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input
                                            ref={editInputRef}
                                            data-testid="edit-prompt-input"
                                            value={editPrompt}
                                            onChange={(e) => setEditPrompt(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitEdit(); }
                                                if (e.key === "Escape") { setPendingEdit(null); setEditPrompt(""); }
                                            }}
                                            placeholder="e.g., make this button pink and larger"
                                            disabled={generating}
                                            className="flex-1 h-9 px-3 bg-background border border-accent font-mono text-xs focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50"
                                        />
                                        <button
                                            data-testid="submit-edit"
                                            onClick={submitEdit}
                                            disabled={!editPrompt.trim() || generating}
                                            className="inline-flex items-center gap-1.5 h-9 px-3 border border-accent bg-accent text-accent-foreground font-mono text-[10px] uppercase tracking-[0.16em] hover:brightness-110 transition disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            {generating ? (
                                                <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
                                            ) : (
                                                <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
                                            )}
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <div className="flex-1 min-h-0 p-4 md:p-6 grid place-items-center overflow-auto loom-scroll">
                        <div
                            className="h-full w-full border border-border bg-white shadow-[0_30px_120px_-30px_hsl(14_100%_62%/0.5),0_0_0_1px_hsl(14_100%_62%/0.15)] rounded-md overflow-hidden transition-[width] duration-200 ease-out"
                            style={{
                                width: DEVICE_WIDTHS[device],
                                maxWidth: "100%",
                            }}
                            data-testid="preview-frame-wrapper"
                        >
                            <iframe
                                key={iframeKey}
                                data-testid="preview-iframe"
                                title="site-preview"
                                srcDoc={previewSrcDoc}
                                sandbox="allow-scripts allow-forms"
                                className="w-full h-full bg-white"
                                style={{ minHeight: "100%" }}
                            />
                        </div>
                    </div>
                </main>
            </div>

            <DeployDialog
                open={publishOpen}
                onOpenChange={setPublishOpen}
                project={project}
                publicUrl={publicUrl}
                publishing={publishing}
                onPublish={doPublish}
                onUnpublish={doUnpublish}
                onRefresh={load}
            />

            <PricingDialog
                open={pricingOpen}
                onOpenChange={(o) => {
                    setPricingOpen(o);
                    if (!o) refreshBilling?.();
                }}
                billing={billing}
            />

            <AssetsDialog
                open={assetsOpen}
                onOpenChange={setAssetsOpen}
                onChange={(count) => setAssetCount(count)}
            />

            <HistoryDialog
                open={historyOpen}
                onOpenChange={setHistoryOpen}
                projectId={projectId}
                currentVersionId={project.current_version_id}
                onPreview={(v) => setPreviewingVersion({ version_number: v.version_number, html: v.html })}
                onRestore={(res) => {
                    setPreviewingVersion(null);
                    setProject((prev) =>
                        prev ? { ...prev, html: res.html, current_version_id: res.current_version_id } : prev,
                    );
                    setIframeKey((k) => k + 1);
                    // refresh messages so the "restored" system message appears
                    api.getProject(projectId).then((fresh) => {
                        setProject(fresh.project);
                        setMessages(fresh.messages || []);
                    }).catch(() => {});
                }}
            />
        </div>
    );
}

function ChatBubble({ role, content, streaming = false }) {
    if (role === "user") {
        return (
            <div className="flex justify-end">
                <div
                    data-testid="chat-bubble-user"
                    className="max-w-[85%] bg-foreground text-background p-3 text-sm leading-relaxed whitespace-pre-wrap"
                    style={{ borderRadius: "2px 2px 0 2px" }}
                >
                    {content}
                </div>
            </div>
        );
    }
    return (
        <div className="flex">
            <div
                data-testid="chat-bubble-assistant"
                className={`max-w-[92%] border border-border bg-card p-3 text-sm leading-relaxed whitespace-pre-wrap font-mono ${
                    streaming ? "loom-caret" : ""
                }`}
                style={{ borderRadius: "2px 2px 2px 0" }}
            >
                {content}
            </div>
        </div>
    );
}
